import { redirect, type LoaderFunctionArgs } from 'react-router'
import {
	buildCentralLoginRedirect,
	introspectCentralSession,
	isCentralSsoEnabled,
} from '@jpg/central-sso'
import { SchoolBrand, Button } from '@jpg/design-system'

/**
 * Root route: redirect to dashboard if authenticated, otherwise show login.
 *
 * When central SSO is enabled, we introspect the session directly here
 * instead of bouncing through the SSO bridge. This avoids an extra
 * redirect round-trip and gets logged-in users straight to the dashboard.
 */
export async function loader(args: LoaderFunctionArgs) {
	// Load .env before any code reads process.env (central-sso, etc.)
	await import('#app/utils/env.server')

	if (!isCentralSsoEnabled()) {
		return null
	}

	// Try to introspect the session directly — avoids an unnecessary
	// redirect through /sso/bridge when the user is already authenticated.
	try {
		const session = await introspectCentralSession(args.request)
		if (session?.email) {
			return redirect('/dashboard')
		}
	} catch {
		// Introspection failed — fall through to login page.
	}

	return null
}

export default function IndexRoute() {
	return (
		<div className="flex items-center justify-center min-h-dvh bg-gradient-to-br from-navy-950 via-navy-800 to-navy-900 p-4 overflow-y-auto">
			<div className="flex flex-col items-center gap-8 p-10 sm:p-12 bg-cream rounded-2xl shadow-2xl max-w-md w-full text-center">
				<SchoolBrand
					variant="full"
					showMotto
					horizontal={false}
					crestSize="xl"
				/>

				<div className="w-12 h-px bg-navy-200" />

				<div className="flex flex-col gap-1">
					<h1 className="font-display uppercase tracking-trajan text-2xl font-bold text-navy">
						JPG Announces
					</h1>
					<p className="font-body text-sm text-navy-500">
						School-wide SMS Announcements via Twilio
					</p>
				</div>

				<Button
					variant="primary"
					size="lg"
					fullWidth
					onClick={() => {
						window.location.href = '/sso/bridge?redirectTo=%2Fdashboard'
					}}
				>
					Sign in with JPG Auth
				</Button>

				<p className="font-ui text-xs text-navy-400">
					Only @jpgacademy.org accounts are allowed
				</p>
			</div>
		</div>
	)
}
